import React, { useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormHelperText,
  TextField,
  Typography,
} from '@mui/material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { differenceInCalendarDays, isValid, parseISO } from 'date-fns';
import { CalendarDays } from 'lucide-react';
import { SegmentedControl } from '../ui';
import { sbg } from '../../theme/sbgTokens';
import { formatCount, formatDateRange, formatNumber } from '../../utils/format';
import { countWorkdays, hoursForRange, toApiDate, validateTimeOffRange } from './time-off/timeOffHours';
import { NO_EMPLOYEE_RECORD, saveErrorMessage, useSaveTimeOffRequest } from './time-off/saveTimeOffRequest';
import type { TimeOffFormData } from './time-off/saveTimeOffRequest';

export type { TimeOffFormData } from './time-off/saveTimeOffRequest';

/**
 * Opened through ModalManager (`openModal({ type: 'time-off-form', data })`),
 * so every prop but the two callbacks ModalManager adds is plain data. The
 * form saves itself (see `useSaveTimeOffRequest`): never pass a save callback
 * through the modal state.
 */
interface TimeOffFormModalProps {
  onClose: () => void;
  /** Edit: the stored request, as GET /time-off/:id returned it. */
  timeOff?: any;
  isEdit?: boolean;
  /** New request: the employee record it is filed for (the signed-in person's). */
  employeeId?: string | null;
  /** Supplied by ModalManager: true while saving, so Escape and the backdrop can't drop the request mid-save. */
  onPendingChange?: (pending: boolean) => void;
}

type FieldErrors = Partial<Record<keyof TimeOffFormData, string>>;

const TYPE_OPTIONS: { value: TimeOffFormData['type']; label: string }[] = [
  { value: 'PTO', label: 'PTO' },
  { value: 'SICK', label: 'Sick' },
  { value: 'WFH', label: 'WFH' },
];

/** Policy notes that the review flags in this app already enforce (see HR time off). */
const TYPE_HELPER: Partial<Record<TimeOffFormData['type'], string>> = {
  PTO: 'PTO requested with less than 2 weeks’ notice is flagged for review.',
  WFH: 'WFH for anyone not on a hybrid schedule needs VP approval.',
};

/** Edit mode receives the stored `YYYY-MM-DD` strings; the date pickers need Dates. */
const toDate = (value: unknown): Date | null => {
  if (!value) return null;
  if (value instanceof Date) return isValid(value) ? value : null;
  if (typeof value === 'string') {
    const parsed = parseISO(value);
    return isValid(parsed) ? parsed : null;
  }
  return null;
};

const formatHours = (hours: number) =>
  `${formatNumber(hours, Number.isInteger(hours) ? 0 : 1)} ${hours === 1 ? 'hour' : 'hours'}`;

const TimeOffFormModal: React.FC<TimeOffFormModalProps> = ({
  onClose,
  timeOff,
  isEdit = false,
  employeeId,
  onPendingChange,
}) => {
  const [formData, setFormData] = useState<TimeOffFormData>(() => ({
    type: timeOff?.type || 'PTO',
    startDate: toDate(timeOff?.startDate),
    endDate: toDate(timeOff?.endDate),
    hours: timeOff?.hours || 0,
    notes: timeOff?.notes || ''
  }));

  const [errors, setErrors] = useState<FieldErrors>({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const submittingRef = useRef(false);
  const saveRequest = useSaveTimeOffRequest({ isEdit, timeOff, employeeId });

  // The stored range of the request being edited, so an untouched range is never re-judged.
  const storedRange = isEdit && timeOff
    ? [toDate(timeOff.startDate), toDate(timeOff.endDate)].map((date) => (date ? toApiDate(date) : null)).join('|')
    : null;

  /**
   * Date errors (time-off/timeOffHours.ts: missing or half-typed, backwards,
   * or no workdays at all). While choosing, an empty or half-typed field isn't
   * flagged yet; submit flags everything. An edit that keeps its stored dates isn't held to
   * the workday rule, so changing only the notes always works.
   */
  const validateDates = (start: Date | null, end: Date | null, next: FieldErrors, submitting = false) => {
    delete next.startDate;
    delete next.endDate;
    const range = validateTimeOffRange(start, end);
    if (!submitting) {
      // Mid-typing a date is half-typed, not wrong: hold those errors for submit.
      if (!start || !isValid(start)) delete range.startDate;
      if (!end || !isValid(end)) delete range.endDate;
    }
    const unchanged = storedRange !== null && start && end && isValid(start) && isValid(end)
      && `${toApiDate(start)}|${toApiDate(end)}` === storedRange;
    if (unchanged && start && end && differenceInCalendarDays(end, start) >= 0) delete range.endDate;
    return { ...next, ...range };
  };

  const handleDateChange = (field: 'startDate' | 'endDate', date: Date | null) => {
    const newFormData = {
      ...formData,
      [field]: date
    };

    if (newFormData.startDate && newFormData.endDate) {
      // Workdays × 8 hours, both ends included; weekends and SBG holidays don't count (see time-off/timeOffHours.ts).
      newFormData.hours = hoursForRange(newFormData.startDate, newFormData.endDate);
    }

    setFormData(newFormData);
    setErrors((current) => validateDates(newFormData.startDate, newFormData.endDate, { ...current }));
  };

  const handleSubmit = async () => {
    const newErrors = validateDates(formData.startDate, formData.endDate, {}, true);
    if (!formData.type) newErrors.type = 'Choose a type.';

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    if (!isEdit && !employeeId) {
      setSubmitError(`Couldn’t send this request. ${NO_EMPLOYEE_RECORD}`);
      return;
    }

    if (submittingRef.current) return;
    submittingRef.current = true;
    setSubmitError(null);
    setSubmitting(true);
    onPendingChange?.(true);
    try {
      // Creates or updates, then raises the success toast. A rejection keeps
      // the form open with every typed value and says what to do next.
      await saveRequest(formData);
      onClose();
    } catch (err) {
      setSubmitError(saveErrorMessage(err, isEdit));
    } finally {
      submittingRef.current = false;
      setSubmitting(false);
      onPendingChange?.(false);
    }
  };

  const workdays = countWorkdays(formData.startDate, formData.endDate);
  const datesReady = Boolean(formData.startDate && formData.endDate && !errors.endDate && !errors.startDate);
  const summary = useMemo(() => {
    if (!formData.startDate || !formData.endDate || !isValid(formData.startDate) || !isValid(formData.endDate)) return null;
    const range = formatDateRange(toApiDate(formData.startDate), toApiDate(formData.endDate));
    return [formatCount(workdays, 'workday'), range, formatHours(formData.hours)];
  }, [formData.startDate, formData.endDate, formData.hours, workdays]);

  const typeHelper = TYPE_HELPER[formData.type];
  const typeLabelId = React.useId();

  return (
    <>
      <DialogTitle>{isEdit ? 'Edit time-off request' : 'Request time off'}</DialogTitle>
      <DialogContent>
        <Typography variant="body1" component="p" sx={{ color: sbg.text.secondary, mb: 2.5 }}>
          {isEdit ? 'Change the type, dates or notes.' : 'Choose the type and the days off.'}
        </Typography>
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <Box sx={{ display: 'grid', gap: 2.5 }}>
            <Box role="group" aria-labelledby={typeLabelId} sx={{ display: 'grid', gap: 0.75, minWidth: 0 }}>
              <Typography id={typeLabelId} component="div" variant="labelMd" sx={{ color: sbg.text.primary }}>
                Type
              </Typography>
              <SegmentedControl<TimeOffFormData['type']>
                aria-label="Time-off type"
                value={formData.type}
                onChange={(value) => {
                  setFormData({ ...formData, type: value });
                  setErrors((current) => {
                    const next = { ...current };
                    delete next.type;
                    return next;
                  });
                }}
                options={TYPE_OPTIONS}
                sx={{ justifySelf: 'start' }}
              />
              {errors.type ? (
                <FormHelperText error sx={{ m: 0 }}>{errors.type}</FormHelperText>
              ) : typeHelper ? (
                <FormHelperText sx={{ m: 0 }}>{typeHelper}</FormHelperText>
              ) : null}
            </Box>

            <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' } }}>
              <DatePicker
                label="Start date"
                value={formData.startDate}
                onChange={(date) => handleDateChange('startDate', date)}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    error: !!errors.startDate,
                    helperText: errors.startDate,
                    sx: { '& input': { fontVariantNumeric: 'tabular-nums' } },
                  }
                }}
              />
              <DatePicker
                label="End date"
                value={formData.endDate}
                minDate={formData.startDate ?? undefined}
                onChange={(date) => handleDateChange('endDate', date)}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    error: !!errors.endDate,
                    helperText: errors.endDate,
                    sx: { '& input': { fontVariantNumeric: 'tabular-nums' } },
                  }
                }}
              />
            </Box>

            <Box
              role="status"
              aria-live="polite"
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.25,
                px: 2,
                py: 1.5,
                borderRadius: sbg.radius.lg,
                bgcolor: sbg.bg.surfaceSubtle,
                color: sbg.text.secondary,
                minWidth: 0,
              }}
            >
              <Box component="span" aria-hidden="true" sx={{ display: 'inline-flex', color: datesReady ? sbg.text.accent : sbg.text.secondary }}>
                <CalendarDays size={16} />
              </Box>
              {summary && datesReady ? (
                <Typography
                  variant="labelMd"
                  component="p"
                  sx={{ color: sbg.text.primary, fontVariantNumeric: 'tabular-nums', display: 'flex', flexWrap: 'wrap', columnGap: 1 }}
                >
                  {summary.map((part, index) => (
                    <React.Fragment key={index}>
                      {index > 0 && <Box component="span" aria-hidden="true" sx={{ color: sbg.border.strong }}>·</Box>}
                      <span>{part}</span>
                    </React.Fragment>
                  ))}
                </Typography>
              ) : (
                <Typography variant="body1" component="p">
                  Choose a start and end date to see the total. Workdays count as 8 hours each; weekends and company holidays aren’t counted.
                </Typography>
              )}
            </Box>

            <TextField
              fullWidth
              label="Notes (optional)"
              placeholder="e.g. Family trip, back on Monday"
              multiline
              minRows={3}
              maxRows={8}
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
            />

            {submitError && <Alert severity="error">{submitError}</Alert>}
          </Box>
        </LocalizationProvider>
      </DialogContent>
      <DialogActions>
        <Button variant="text" onClick={onClose} disabled={submitting}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          disabled={submitting}
          variant="contained"
          aria-busy={submitting || undefined}
          startIcon={submitting ? <CircularProgress size={16} thickness={5} color="inherit" aria-hidden="true" /> : undefined}
        >
          {submitting ? (isEdit ? 'Saving…' : 'Sending request…') : isEdit ? 'Save changes' : 'Send request'}
        </Button>
      </DialogActions>
    </>
  );
};

export default TimeOffFormModal;
